/**
 * Care Progress Monitor — /whats-new/care-progress-monitor
 *
 * Standalone launch 1-pager. Renders the doctor-side value story
 * (longitudinal timeline, milestone editor, cross-highlight) alongside
 * the patient-side value story (curated progress card inside the
 * OTP-gated DHF viewer). Deliberately kept single-file so the page
 * stays snappy and content-shift-free for Google's Discover crawl.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Share2, Copy, Check, Target, Pill, FlaskConical,
  ArrowRight, Activity, Users,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/care-progress-monitor';
const SHARE_MESSAGE = (
  `DocSite just launched Care Progress Monitor — a straight-line view of every visit, medicine change, test result and improvement milestone on one thread.\n\n` +
  `• Doctor-side: hover a milestone → highlights the medicines pursuing it. Hover a medicine → highlights the goals it's chasing.\n\n` +
  `• Patient-side: a "Your care progress · X of Y goals hit" card inside the OTP-gated DHF viewer, listing each medicine driving each goal.\n\n` +
  `Read the launch: ${SHARE_URL}`
);

const PILLARS = [
  { icon: Activity, tag: 'Doctor · Timeline', title: 'One straight line for the whole condition',
    body: 'Every visit, every medicine dose change, every test advised, every lab report received — plotted chronologically on a single vertical stream. No hunting through old DHFs.' },
  { icon: Target, tag: 'Doctor · Milestones', title: 'Improvement targets, per condition',
    body: 'Set 1–6 week (or custom to 26w) improvement milestones during any DHF visit. Mark each hit, missed, or reopened as your patient progresses. Merged automatically across visits so re-declaring the same target doesn\'t duplicate.' },
  { icon: Pill, tag: 'Composer · Linkage', title: 'Every medicine ticks the goals it pursues',
    body: 'Inside each medicine card in the composer, a "Contributes to →" chip strip lists that condition\'s milestones. Two medicines can tick the same milestone (combination therapy) — the patient sees "Pursued by Telmi + Amlo".' },
  { icon: FlaskConical, tag: 'Legacy support', title: 'Historical DHFs join automatically',
    body: 'Doctors with years of sealed DHFs can Backfill from prior DHFs in one click — even for legacy files that never had a "condition" tag. DocSite synthesises a care item from the reason for visit so every past patient shows up.' },
  { icon: Users, tag: 'Patient view', title: 'A curated progress card inside the DHF viewer',
    body: 'When your patient opens their DHF link (view-token + OTP gated), they see a violet card with the goals YOU set and the medicines pursuing each one. Every goal shows HIT / ON TRACK / MISSED — no clinical jargon.' },
];

const NUMBERS = [
  { n: '1',   label: 'Single vertical timeline that fuses visits, medicine deltas, tests, lab reports and milestones' },
  { n: '5',   label: 'Event types on the stream: visit · med change · test advised · test received · milestone opened/hit/missed' },
  { n: '4',   label: 'Milestone status states: open · hit · missed · cancelled — each a coloured chip' },
  { n: '26',  label: 'Weeks maximum on a custom target. Shortest is 1 week — no zero-day tricks' },
  { n: '96',  label: 'Percent of the doctor\'s viewport the timeline modal now fills — persistent, X-only close' },
  { n: '0',   label: 'Patient authorisation prompts needed — this is doctor-owned DpD under DPDP §7' },
];

export default function CareProgressMonitorLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(SHARE_MESSAGE); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: 'DocSite · Care Progress Monitor', text: SHARE_MESSAGE, url: SHARE_URL }); } catch { /* cancelled */ } }
    else doCopy();
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: 'Care Progress Monitor — a straight-line view of every visit, medicine, test and milestone on one thread',
    datePublished: '2026-08-11',
    author: { '@type': 'Organization', name: 'DocSite' },
    publisher: { '@type': 'Organization', name: 'DocSite', logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo.png' } },
    mainEntityOfPage: SHARE_URL,
    description: 'DocSite ships Care Progress Monitor — a longitudinal thread view fusing visits, medicine changes, lab reports and doctor-authored improvement milestones. Patients see a curated version inside their DHF viewer.',
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>Care Progress Monitor — longitudinal patient thread view · DocSite</title>
        <meta name="description" content="A straight-line view of every visit, medicine change, test result and improvement milestone on one thread — for the doctor's dashboard and the patient's DHF portal." />
        <link rel="canonical" href={SHARE_URL} />
        <meta property="og:title" content="Care Progress Monitor · DocSite" />
        <meta property="og:description" content="Every visit, medicine, test result and improvement milestone on one straight line — doctor-side and patient-side." />
        <meta property="og:url" content={SHARE_URL} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 md:py-14">
        <nav className="text-xs text-slate-500 mb-4" aria-label="Breadcrumb">
          <Link to="/whats-new" className="hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to What's New</Link>
        </nav>

        <header className="max-w-4xl mb-10">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-violet-700 flex items-center gap-1.5">
            <Sparkles size={12} /> 11 August 2026 · Care Progress Monitor
          </p>
          <h1 className="font-medium text-slate-900 mt-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 3.5vw, 3.75rem)', lineHeight: 1.1 }}>
            Every visit, every dose change, every test — on one straight line.
          </h1>
          <p className="text-base text-slate-600 mt-4 max-w-2xl leading-relaxed">
            The Care Progress Monitor is DocSite's answer to a chronic-care question doctors ask every day: <em>"how is this patient actually doing over time?"</em> It fuses their entire visit history — clinical notes, medicine changes with dose deltas, tests advised, lab reports received, and the improvement milestones you set — into a single vertical stream, per condition. Ships today, backward-compatible with every DHF you've already sealed.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={share} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700"><Share2 size={14} /> Share this launch</button>
            <button onClick={doCopy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300">
              {copied ? <><Check size={14} className="text-emerald-600" /> Copied</> : <><Copy size={14} /> Copy the story</>}
            </button>
          </div>
        </header>

        <section className="max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <article key={i} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-100 text-violet-700"><Icon size={16} /></span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-violet-700">{p.tag}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.body}</p>
              </article>
            );
          })}
        </section>

        <section className="max-w-4xl mb-12">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-slate-500 mb-3">By the numbers</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NUMBERS.map((k, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4">
                <div className="text-3xl font-black text-violet-700 leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>{k.n}</div>
                <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">{k.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mb-12 rounded-2xl bg-white border border-slate-200 p-6">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-emerald-700 mb-2">Legal footing</p>
          <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Doctor-owned DpD — no patient authorisation required.</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            The Care Progress Monitor is a distillation of the doctor's own clinical work-product across their own sealed DHFs. Under DPDP Act 2023 §7 ("legitimate use"), a treating physician holds a lawful basis to view the longitudinal record of a patient they've cared for. This is why the monitor opens instantly on your dashboard — no OTP, no share-token, no re-consent. What the <em>patient</em> sees on their DHF portal remains OTP-gated + encrypted-at-rest, exactly as it was before.
          </p>
        </section>

        <div className="max-w-4xl rounded-2xl border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>See it on your patient roster.</p>
            <p className="text-xs text-slate-500 mt-0.5">Every historical DHF back-fills into a thread in one click. Zero migration needed.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/register?as=doctor&from=care_progress_monitor" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">Start free in 3 clicks <ArrowRight size={14} /></Link>
            <Link to="/whats-new" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300">← Back to launch timeline</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
